"use client";
import { Box, Typography, Button, useTheme, ClickAwayListener, useMediaQuery } from "@mui/material";
import { useTranslations } from "next-intl";

interface GroupModalProps {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
}

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  handleTelegramClick: () => void;
  groupModalTitle: string;
  telegramSupport: string;
  wechatText: string;
}

// 独立的 BottomSheet 组件
function BottomSheet({ open, onClose, handleTelegramClick, groupModalTitle, telegramSupport, wechatText }: BottomSheetProps) {
  const theme = useTheme();

  if (!open) return null;

  return (
    <>
      {/* 背景遮罩 */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1299,
        }}
      />

      {/* Bottom Sheet */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          backgroundColor: theme.palette.background.paper,
          borderTopLeftRadius: "1rem",
          borderTopRightRadius: "1rem",
          padding: "2rem 1.5rem 4rem",
          boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
          transform: open ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.3s ease-out",
        }}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "var(--font-size-s)",
            lineHeight: 1.6,
            textAlign: "center",
            mb: 6,
          }}>
          {groupModalTitle}
        </Typography>

        {/* Telegram 按钮 */}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleTelegramClick}
          sx={{
            fontSize: "var(--font-size-s)",
            fontWeight: 600,
            py: 1,
            px: 2,
            mb: 3,
            borderRadius: "2rem",
            width: "100%",
            height: "3rem",
          }}>
          {telegramSupport}
        </Button>

        {/* 二维码区域 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: {xs: 1, sm: 2},
          }}>
          <Box
            sx={{
              width: "12.5rem",
              height: "12.5rem",
              backgroundColor: "#FFFFFF",
              border: "2px solid #E1E4E5",
              borderRadius: "0.75rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}>
            <img
              src="/images/wechat-qr-code.jpg"
              alt="WeChat QR Code"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          {/* WeChat 文本 */}
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "var(--font-size-xs)",
              textAlign: "center",
              lineHeight: 1.5,
            }}>
            {wechatText}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default function GroupModal({ open, onClose, anchorEl }: GroupModalProps) {
  const theme = useTheme();
  const t = useTranslations("FloatingButtons");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleTelegramClick = () => {
    window.open("https://t.me/RightPulseServiceGroup", "_blank", "noopener,noreferrer");
  };

  // 计算弹窗位置
  const getPosition = () => {
    if (!anchorEl) return { top: 0, left: 0 };

    const rect = anchorEl.getBoundingClientRect();
    return {
      top: rect.top + rect.height / 2, // 垂直居中对齐按钮
      left: rect.left - 8, // 按钮左侧，留出间距
    };
  };

  const position = getPosition();

  if (!open) return null;

  // 移动端使用 BottomSheet 组件
  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        handleTelegramClick={handleTelegramClick}
        groupModalTitle={t("groupModalTitle")}
        telegramSupport={t("telegramSupport")}
        wechatText={t("wechatText")}
      />
    );
  }

  // 桌面端原有样式
  return (
    <ClickAwayListener onClickAway={onClose}>
      <Box
        sx={{
          position: "fixed",
          top: position.top,
          left: position.left,
          transform: "translate(-100%, -50%)", // 向左偏移整个宽度，垂直居中
          zIndex: 1300,
          width: "16.125rem",
          padding: "0.5rem 0.75rem 1rem",
          borderRadius: "0.5rem",
          backgroundColor: theme.palette.mode === "dark" ? "rgba(20, 22, 24, 0.85)" : "rgba(255, 255, 255, 0.85)",
          border: `1px solid ${theme.palette.mode === "dark" ? "#2D3135" : "#E1E4E5"}`,
          boxShadow:
            theme.palette.mode === "dark" ? "0 20px 40px rgba(0, 0, 0, 0.5)" : "0 20px 40px rgba(0, 0, 0, 0.1)",
        }}>
        {/* 标题文本 */}
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            fontSize: "var(--font-size-2xs)",
            lineHeight: 1.6,
            fontWeight: 400,
          }}>
          {t("groupModalTitle")}
        </Typography>

        {/* Telegram 按钮 */}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleTelegramClick}
          sx={{
            fontSize: "var(--font-size-2xs)",
            fontWeight: 600,
            py: 0.5,
            px: 1.5,
            my: 2,
            borderRadius: "2rem",
            width: "100%",
          }}>
          {t("telegramSupport")}
        </Button>

        {/* 二维码区域 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 1,
          }}>
          <Box
            sx={{
              flexShrink: 0,
              width: "5rem",
              height: "5rem",
              backgroundColor: "#FFFFFF",
              border: "2px solid #E1E4E5",
              borderRadius: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              ml: 0.5,
              overflow: "hidden",
            }}>
            <img
              src="/images/wechat-qr-code.jpg"
              alt="WeChat QR Code"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          {/* WeChat 文本 */}
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "var(--font-size-2xs)",
            }}>
            {t("wechatText")}
          </Typography>
        </Box>
      </Box>
    </ClickAwayListener>
  );
}
